/**
 * REST API wrapper для client-webapp (конечный VPN-юзер в дочернем боте партнёра).
 *
 * Auth:
 *  - bot_id берётся из URLSearchParams (передаётся в menu_button URL дочернего бота
 *    как `?bot_id=<uuid>`, см. bot_config.py:configure_child_bot)
 *  - POST /api/client/auth/telegram-webapp с {bot_id, init_data} → JWT
 *
 * Fallback: при отсутствии VITE_API_BASE или 4xx auth → auto-fallback к mock.ts (dev).
 */

import { getWebApp, setBrandColors, applyBrandPrimary } from './telegram';
import i18n from '@/i18n';
import { applyLocale, applyTextOverrides, setHideWhitelist } from '../i18n';
import type {
  Subscription,
  BlockedDevice,
  Device,
  ActivityLog,
  Plan,
  ClientProfile,
  Review,
  ReviewsPage,
} from '@/api/types';

/** Вкладки витрины отзывов. */
export type ReviewFilter = 'all' | 'positive' | 'negative';

const API_BASE = import.meta.env.VITE_API_BASE || '';
// Fail-closed: mock включается ТОЛЬКО явным '=true'. Любое другое значение
// (unset / опечатка / 'False' / '0') = mock выключен. Иначе забытая prod-переменная
// молча подсунула бы фейковые цены/балансы/подписки вместо ошибки.
const USE_MOCK_FALLBACK = import.meta.env.VITE_USE_MOCK_FALLBACK === 'true';

// Preconnect: DNS + TLS до первого fetch — экономия 100-300ms на cold start.
if (typeof document !== 'undefined' && API_BASE) {
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = API_BASE;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  } catch {
    // ignore
  }
}

interface AuthState {
  token: string | null;
  clientId: string | null;
  partnerId: string | null;
  botId: string | null;
  expiresAt: number;
  /** Партнёр может убрать раздел «Статус сети» у своего бота. Приходит в auth-ответе,
   *  чтобы меню отрисовалось правильно с первого кадра, без отдельного запроса. */
  statusPageEnabled: boolean;
  reviewsEnabled: boolean;
  webappEnabled: boolean;
  /** Подтверждать ли покупку биометрией. null — клиент ещё не выбирал, и экран
   *  оплаты один раз предложит выбор. Приходит в auth-ответе, потому что нужен
   *  до первой отрисовки кнопки оплаты. */
  biometricConfirmEnabled: boolean | null;
  /** Показывать ли предложение «Свой VPN» (карточка на главной, строка в профиле).
   *  Тот же флаг, что у кнопки в боте: рубильник платформы и тумблер партнёра. */
  mirrorsEnabled: boolean;
  /** Открыт ли у бота раздел «Навсегда». По нему экран тарифов решает, показывать ли
   *  вкладки «На срок / Навсегда». */
  lifetimeEnabled: boolean;
}

let auth: AuthState = {
  token: null,
  clientId: null,
  partnerId: null,
  botId: null,
  expiresAt: 0,
  statusPageEnabled: true,
  reviewsEnabled: true,
  webappEnabled: true,
  biometricConfirmEnabled: null,
  mirrorsEnabled: false,
  lifetimeEnabled: false,
};
let authPromise: Promise<AuthState> | null = null;

export class ApiError extends Error {
  /** Сырой технический ярлык (`POST /path → 422`) — для console/Sentry, не для юзера. */
  technical: string;
  /** Код ошибки бэка (`app.adaptgroup.insufficient_funds`), по нему подбирается перевод. */
  code: string | null;
  constructor(public status: number, public body: unknown, technical: string) {
    // Бэк отдаёт `{error:{code,message}}` — человеческое сообщение кладём в .message,
    // чтобы любой `toast(e.message)` показывал нормальный текст, а не URL+код.
    const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
    super(err?.message || technical);
    this.technical = technical;
    this.code = err?.code ?? null;
  }
}

/**
 * Ошибка поставщика сети при выдаче подписки: AdaptGroup (`app.adaptgroup.*`) или
 * FranCenter, раздел «Навсегда» (`app.supplier.*`). Бэк уже прислал текст на русском,
 * но клиент может читать по-английски, поэтому переводим по коду сами.
 */
export function isProviderError(e: unknown): boolean {
  return (
    e instanceof ApiError &&
    !!e.code &&
    (e.code.startsWith('app.adaptgroup.') || e.code.startsWith('app.supplier.'))
  );
}

/**
 * Block B.9: специальная ошибка для 401/403 на auth или защищённых эндпоинтах.
 * Поднимается БЕЗ fallback на mock — React error boundary поймает и отрендерит
 * `<BotDisabledScreen>` вместо ложно-работающего UI.
 */
export class BotDisabledError extends ApiError {
  constructor(status: number, body: unknown, message: string) {
    super(status, body, message);
    this.name = 'BotDisabledError';
  }
}

/**
 * Telegram не отдал данные для входа: SDK не загрузился либо initData пустая.
 *
 * Это НЕ отказ бэка — запрос вообще не уходил, в серверных логах такого случая нет.
 * Раньше подставлялся `ApiError(401)` → клиент видел «Бот временно недоступен» и шёл
 * ругаться к партнёру, хотя бот работал: у клиента оборвалась связь или недоступен
 * telegram.org (свой VPN, блокировки).
 */
export class TelegramUnavailableError extends Error {
  constructor(public reason: 'no_sdk' | 'no_init_data') {
    super(reason === 'no_sdk' ? 'Telegram SDK is not loaded' : 'Telegram initData is empty');
    this.name = 'TelegramUnavailableError';
  }
}

const BOT_ID_STORAGE_KEY = 'bot_id';

/** Ключ кэша основного цвета. Обязательно с bot_id: адрес мини-аппа общий у всех
 *  ботов платформы, поэтому общий и localStorage. */
const brandKey = (botId: string) => `brand_primary:${botId}`;

/**
 * bot_id приходит в menu_button URL (`?bot_id=<uuid>`), но живёт там недолго:
 * TanStack Router при навигации search-параметры не тащит, поэтому после первого
 * же перехода URL становится просто `/plans`. Пока JWT лежит в памяти, это незаметно
 * — а после перезагрузки страницы (или когда токен протух) авторизация падала с
 * «Missing bot_id in URL», и клиент видел ошибку на ровном месте.
 *
 * Поэтому запоминаем, но в sessionStorage: он живёт внутри одного запуска вебвью и
 * переживает reload, а в соседнего бота не доедет физически. В localStorage значение
 * оставалось навсегда, и мини-апп, открытый без `?bot_id=`, авторизовался бы под
 * прошлым ботом целиком — вместе с балансом и подписками.
 */
export function currentBotId(): string | null {
  return getBotId();
}

function getBotId(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const fromUrl =
    new URLSearchParams(window.location.search).get('bot_id') ||
    new URLSearchParams(hash).get('bot_id');
  if (fromUrl) {
    try {
      sessionStorage.setItem(BOT_ID_STORAGE_KEY, fromUrl);
    } catch {
      // приватный режим / переполнено — работаем как раньше, из URL
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(BOT_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

async function authenticate(): Promise<AuthState> {
  if (auth.token && Date.now() < auth.expiresAt - 30_000) return auth;
  if (authPromise) return authPromise;

  authPromise = (async () => {
    // NB: токен НЕ кешируем в CloudStorage между сессиями. brand-палитра (в т.ч.
    // основной цвет) приходит в auth-ответе, и партнёр может менять её в любой
    // момент — поэтому на каждый холодный старт делаем свежий auth, чтобы цвет
    // подхватывался при переоткрытии Mini App. В рамках сессии работает in-memory
    // кеш `auth` выше, лишних запросов нет.

    const tg = getWebApp();
    const initData = tg?.initData ?? '';
    const botId = getBotId();
    if (!tg) {
      throw new TelegramUnavailableError('no_sdk');
    }
    if (!initData) {
      throw new TelegramUnavailableError('no_init_data');
    }
    if (!botId) {
      throw new ApiError(400, null, i18n.t('Open from bot menu'));
    }
    const res = await fetch(`${API_BASE}/api/client/auth/telegram-webapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bot_id: botId, init_data: initData }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new ApiError(res.status, body, `Auth failed: ${res.status}`);
    }
    const data = (await res.json()) as {
      access_token: string;
      expires_in: number;
      client_id: string;
      partner_id: string;
      bot_id: string;
      brand?: { header_color?: string; bg_color?: string; primary_color?: string };
      webapp_texts?: Record<string, string>;
      status_page_enabled?: boolean;
      reviews_enabled?: boolean;
      webapp_enabled?: boolean;
      biometric_confirm_enabled?: boolean | null;
      mirrors_enabled?: boolean;
      lifetime_enabled?: boolean;
      hide_whitelist?: boolean;
      locale?: string;
    };
    auth = {
      token: data.access_token,
      clientId: data.client_id,
      partnerId: data.partner_id,
      botId: data.bot_id,
      expiresAt: Date.now() + data.expires_in * 1000,
      // Старый бэк без этого поля — раздел показываем (как и при отсутствии ключа в БД).
      statusPageEnabled: data.status_page_enabled !== false,
      reviewsEnabled: data.reviews_enabled !== false,
      webappEnabled: data.webapp_enabled !== false,
      biometricConfirmEnabled: data.biometric_confirm_enabled ?? null,
      // Старый бэк без поля: предложение не показываем, у бота оно и так есть.
      mirrorsEnabled: data.mirrors_enabled === true,
      // Старый бэк без поля: раздела «Навсегда» нет.
      lifetimeEnabled: data.lifetime_enabled === true,
    };

    // Применить brand-палитру из auth response. Сначала основной цвет (ведёт
    // нативную шапку), затем явные header/bg — чтобы они имели приоритет.
    // Звать безусловно: у бота без своего цвета ответ приходит пустым, и раньше
    // тема просто оставалась от предыдущего бота. Теперь пустое значение сбрасывает
    // её к дефолту, а кэш чистится, чтобы не воскреснуть на следующем старте.
    applyBrandPrimary(data.brand?.primary_color);
    setBrandColors(data.brand?.header_color, data.brand?.bg_color);
    try {
      if (data.brand?.primary_color) {
        localStorage.setItem(brandKey(botId), data.brand.primary_color);
      } else {
        localStorage.removeItem(brandKey(botId));
      }
    } catch {
      // ignore
    }

    // Язык клиента из БД — до первой отрисовки. Раньше в i18n был прибит `lng: 'ru'`,
    // и англоязычный клиент видел русский экран, пока не тапнет переключатель.
    // Ставим ДО наложения override: они одноязычные и не должны зависеть от порядка.
    if (data.locale) applyLocale(data.locale);

    // Наложить партнёрские override текстов мини-аппа (одноязычные, поверх i18n-дефолтов).
    applyTextOverrides(data.webapp_texts);
    // Нейтральные тексты (без белых списков, обходов и анонимности), если партнёр включил у бота.
    setHideWhitelist(data.hide_whitelist === true);

    return auth;
  })();
  try {
    return await authPromise;
  } finally {
    authPromise = null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { token } = await authenticate();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => null);
    throw new ApiError(res.status, errBody, `${method} ${path} → ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function tryRequest<T>(method: string, path: string, fallback: () => T, body?: unknown): Promise<T> {
  // Мутации (POST/PATCH/DELETE/PUT) НИКОГДА не fallback'ят на mock — иначе UI выглядит
  // как «сработало» при упавшем бэке, юзер думает что купил подписку/применил промо/удалил
  // устройство, по факту ничего не произошло. Только GET может молча отдать пустоту в dev.
  const isMutation = method.toUpperCase() !== 'GET';
  const fallbackAllowed = USE_MOCK_FALLBACK && !isMutation;

  if (!API_BASE) {
    if (fallbackAllowed) return fallback();
    throw new ApiError(0, null, 'VITE_API_BASE not configured');
  }
  try {
    return await request<T>(method, path, body);
  } catch (e) {
    // Нет данных от Telegram — пробрасываем как есть: бот тут ни при чём, экран
    // «бот недоступен» посылал бы клиента ругаться не по адресу.
    if (e instanceof TelegramUnavailableError) throw e;
    // Block B.9: 401/403 — бот отключён партнёром, аккаунт заблокирован, или auth протух.
    // НЕ fallback'им на mock (иначе клиент видит фейковые данные при отключённом боте).
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      throw new BotDisabledError(e.status, e.body, e.message);
    }
    if (fallbackAllowed) {
      console.warn('[client-api] GET fallback to mock:', path, e);
      return fallback();
    }
    throw e;
  }
}

// ===== Адаптеры backend → frontend shape =====
// Бэк отдаёт snake_case + minor units, фронт работает с camelCase + ГБ/рублями.

interface BackendPlan {
  uuid: string;
  name: string;
  emoji?: string | null;
  days: number;
  devices: number;
  traffic_limit_bytes: number | null;
  retail_price_minor: number;
  retail_price_display: number;
  total_price_display?: number;
  fee_display_minor?: number;
  display_currency: string;
  is_special?: boolean;
  already_purchased?: boolean;
  discounted_price_display?: number | null;
  promo_percent?: number | null;
  promo_expires_at?: string | null;
  is_lifetime?: boolean;
  is_popular?: boolean;
}

function backendPlanToPlan(p: BackendPlan): Plan {
  return {
    id: p.uuid,
    name: p.name,
    emoji: p.emoji || null,
    days: p.days,
    devices: p.devices,
    trafficGb: p.traffic_limit_bytes ? Math.floor(p.traffic_limit_bytes / 1024 / 1024 / 1024) : null,
    // Показываем цену со сбором: у партнёров, включивших его, она одинакова для всех
    // способов оплаты, и надбавки на чекауте человек уже не увидит.
    priceRub: Math.floor((p.total_price_display || p.retail_price_display) / 100),
    balancePriceRub: Math.floor(p.retail_price_display / 100),
    feeRub: Math.floor((p.fee_display_minor ?? 0) / 100),
    discountedPriceRub:
      p.discounted_price_display != null ? Math.floor(p.discounted_price_display / 100) : null,
    promoPercent: p.promo_percent ?? null,
    promoExpiresAt: p.promo_expires_at ?? null,
    tags: p.is_popular ? ['popular'] : [],
    isSpecial: p.is_special ?? false,
    alreadyPurchased: p.already_purchased ?? false,
    isLifetime: p.is_lifetime ?? false,
  };
}

interface BackendSubscription {
  id: string;
  plan_uuid: string;
  plan_name: string;
  devices: number;
  days: number;
  end_date: string;
  days_left?: number;
  is_active: boolean;
  is_frozen: boolean;
  frozen_at?: string | null;
  is_trial?: boolean;
  used_traffic_bytes: number | null;
  traffic_limit_bytes: number | null;
  auto_renew_enabled: boolean;
  auto_renew_days?: number | null;
  subscription_url: string | null;
  link_rotation_available_at?: string | null;
  is_lifetime?: boolean;
  supplier?: 'adaptgroup' | 'scvpn';
  addons_available?: boolean;
}

function backendSubToSub(s: BackendSubscription): Subscription {
  return {
    id: s.id,
    planId: s.plan_uuid,
    planName: s.plan_name,
    days: s.days,
    devices: s.devices,
    status: s.is_frozen ? 'frozen' : s.is_active ? 'active' : 'expired',
    expiresAt: s.end_date,
    daysLeft: s.days_left,
    frozenAt: s.frozen_at ?? null,
    trafficUsedGb: s.used_traffic_bytes ? +(s.used_traffic_bytes / 1024 / 1024 / 1024).toFixed(2) : 0,
    trafficTotalGb: s.traffic_limit_bytes ? Math.floor(s.traffic_limit_bytes / 1024 / 1024 / 1024) : null,
    configUrl: s.subscription_url || '',
    autoRenew: s.auto_renew_enabled,
    autoRenewDays: s.auto_renew_days ?? null,
    isTrial: Boolean(s.is_trial),
    linkRotationAvailableAt: s.link_rotation_available_at ?? null,
    isLifetime: Boolean(s.is_lifetime),
    supplier: s.supplier ?? 'adaptgroup',
    addonsAvailable: Boolean(s.addons_available),
  };
}

interface BackendDevice {
  id: number;
  hwid: string | null;
  name: string;
  os: string | null;
  model: string | null;
  os_version: string | null;
  ip: string | null;
  first_seen: string | null;
  last_seen: string | null;
}

function backendDeviceToDevice(d: BackendDevice): Device {
  return {
    id: String(d.id),
    name: d.name,
    os: d.os || 'Unknown',
    model: d.model || '',
    lastSeen: d.last_seen || '',
    firstSeen: d.first_seen || '',
    ip: d.ip || '',
    geo: '',
    isNew: false,
  };
}

// ===== Public API =====

// --- Статус VPN-сети (/status) ---------------------------------------------
export interface StatusUptimeDay {
  day: string;
  pct: number;
  status: string;
}
export interface StatusCountryItem {
  flag: string;
  country_ru: string;
  /** ISO-код («DE») с сервера: по нему картинка флага и имя страны на английском. */
  country_code?: string | null;
  up: number;
  total: number;
}
export interface StatusComponent {
  status: string;
  uptime_pct_90d: number | null;
  days: StatusUptimeDay[];
}
export interface StatusNetwork {
  countries: number;
  regions: Record<string, StatusCountryItem[]>;
  checked_at: string | null;
}
/** Сеть одного раздела: у «На срок» и «Навсегда» поставщики разные, и серверы свои. */
export interface StatusSectionNetwork {
  component: StatusComponent;
  network: StatusNetwork;
}
export interface ClientStatusResponse {
  /** Статус сети того раздела, которым пользуется клиент (`primary_section`). */
  overall: string;
  updated_at: string | null;
  /** Сеть «На срок». */
  component: StatusComponent;
  network: StatusNetwork;
  /** Необязательно: бэк до выкатки раздела «Навсегда» полей не отдаёт, это «На срок». */
  primary_section?: 'term' | 'lifetime';
  /** Сеть «Навсегда»: только если бот продаёт раздел или у клиента есть такая подписка. */
  lifetime?: StatusSectionNetwork | null;
}

// ---------- Розыгрыши ----------

export interface GiveawayTaskState {
  id: string;
  kind: 'channel' | 'invite';
  done: boolean;
  current: number;
  required: number;
  channel_url: string | null;
  channel_name: string | null;
  /** Проверку выполнить не удалось (Telegram не ответил) — не то же самое, что «не выполнено». */
  errored: boolean;
}

export interface Giveaway {
  id: string;
  title: string;
  description: string | null;
  status: 'active' | 'drawing' | string;
  ends_at: string;
  winners_count: number;
  prize_name: string;
  entries_count: number;
  joined: boolean;
  won: boolean;
  tasks: GiveawayTaskState[];
  can_join: boolean;
  /** Ссылка для задания «приведи друзей». Есть и при выключенной реферальной программе. */
  invite_link: string | null;
  /** Платит ли партнёр бонусы по ссылке. Без этого не обещаем «получите процент». */
  referral_enabled: boolean;
}

// ---------- Вывод реферального заработка (идея #162) ----------

/**
 * `ton` (GRAM на кошелёк) и `cryptobot` (чек к Telegram клиента) может заплатить сервис
 * автоматически, с комиссией сети из суммы (заявки #183, #186). Остальные переводит
 * владелец сервиса руками.
 */
export type WithdrawMethod = 'card' | 'sbp' | 'usdt_trc20' | 'ton' | 'cryptobot';
/**
 * `pending` ждёт перевода, клиент может отменить. `processing` значит, что владелец
 * сервиса нажал «Перевожу» и деньги уже в пути: отмены нет, иначе клиент получил бы и
 * перевод, и возврат суммы на баланс. Обе открытые, остальные закрытые.
 */
export type WithdrawStatus = 'pending' | 'processing' | 'paid' | 'rejected' | 'cancelled';

export interface WithdrawalItem {
  id: string;
  status: WithdrawStatus;
  /** Minor-единицы валюты показа: 50000 = 500 ₽. */
  amount_display_minor: number;
  currency: string;
  method: WithdrawMethod;
  /**
   * Уже в человеческом виде: карта по четыре цифры, телефон с банком, адрес TRC20.
   * У закрытых заявок бэк отдаёт его замаскированным (`•••• 1111`, `TQn9…bLSE`),
   * показываем как есть.
   */
  destination: string;
  created_at: string;
  decided_at: string | null;
  reject_reason: string | null;
  /** Комментарий к переводу GRAM на биржу. */
  memo?: string | null;
  /** `platform`: платит сервис автоматически, `manual`: владелец сервиса руками. */
  payout_mode?: 'manual' | 'platform';
  /** Сколько придёт при автоматической выплате (сумма минус комиссия). */
  net_display_minor?: number | null;
  transfer_state?: string | null;
  /** Чек CryptoBot: забрать можно и отсюда, если сообщение в чате потерялось. */
  check_url?: string | null;
  /** Хэш перевода GRAM для ссылки на обозреватель сети. */
  tx_hash?: string | null;
}

/**
 * Состояние вывода у клиента. Суммы в minor валюты показа, `earned` и `available`
 * бэк уже округлил вниз до целых рублей: вывести можно только целую сумму.
 */
export interface WithdrawState {
  /** Партнёр включил вывод. Выключен — формы нет, но заявку, которую ещё не начали
   *  переводить (`pending`), можно отменить. */
  enabled: boolean;
  methods: WithdrawMethod[];
  currency: string;
  min_display_minor: number;
  /** Минимум по способу: у GRAM и CryptoBot не меньше минимума выплат (100 ₽). */
  method_min?: Partial<Record<WithdrawMethod, number>>;
  /** Способы, которые сервис может заплатить автоматически: для них есть котировка. */
  platform_methods?: WithdrawMethod[];
  /** Всё заработанное по рефералке за всё время. */
  earned_display_minor: number;
  /**
   * Сколько можно вывести сейчас: заработанное за вычетом выведенного, отложенного в
   * заявке и потраченного. Покупка с баланса сначала тратит заработанное по рефералке,
   * поэтому цифра бывает меньше `earned` и без единого вывода.
   */
  available_display_minor: number;
  open: WithdrawalItem | null;
  /** Новые сверху, не больше 20, открытая заявка тоже здесь. */
  history: WithdrawalItem[];
}

export interface CreateWithdrawalBody {
  /** Целые единицы × 100: копеек бэк не принимает (`not_whole`). */
  amount_display_minor: number;
  method: WithdrawMethod;
  /** Для чека CryptoBot пусто: чек привязывается к Telegram клиента. */
  destination: string;
  /** Только для СБП. */
  bank?: string;
  /** Только для GRAM: комментарий для биржи. */
  memo?: string;
}

/** Комиссия и сумма «на руки» при автоматической выплате, посчитанные бэком. */
export interface WithdrawQuote {
  method: WithdrawMethod;
  amount_display_minor: number;
  fee_display_minor: number;
  net_display_minor: number;
  min_display_minor: number;
  currency: string;
}

/** Цена продления, посчитанная бэком: наценка бота, курс и округление живут там. */
export interface RenewQuote {
  days: number | null;
  plan_days: number;
  price_display_minor: number;
  /** К оплате счётом: со сбором, если партнёр встроил его в цену. */
  total_display_minor?: number;
  /** Сбор внутри total_display_minor. */
  fee_display_minor?: number;
  price_before_discount_display_minor: number;
  discount_display_minor: number;
  price_usd_minor: number;
  promo_code: string | null;
  // 'partner_funds' — промокод есть, но партнёру нечем оплатить свою скидку.
  // Цена в ответе полная, продлить можно; причину надо показать.
  promo_blocked_reason: string | null;
  currency: string;
  new_end_date: string;
  balance_display_minor: number;
  balance_enough: boolean;
  min_days: number;
  max_days: number;
  /** Сумма счёта по каждому способу оплаты, со сбором способа (minor). Без звёзд. */
  provider_totals?: Record<string, number>;
}

/**
 * Ключ одного намерения купить докупку. Живёт до успешной покупки: двойное нажатие
 * и повтор после сбоя несут тот же ключ, и бэк второй раз не списывает и не покупает
 * у поставщика. `getRandomValues`, а не `randomUUID`: тот есть не во всех WebView.
 */
export function newIntentNonce(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Страны в подписке клиента (заявка #169). */
export interface SubscriptionCountries {
  /** Владелец сервиса разрешил выбирать страны. */
  enabled: boolean;
  section: 'term' | 'lifetime';
  countries: Array<{
    code: string;
    flag: string;
    name: string;
    region: string;
    servers: number;
    /** Сколько серверов страны с лимитом трафика (обход белых списков). */
    limited: number;
  }>;
  /** Коды стран, которые клиент убрал. Пусто — все. */
  hidden: string[];
}

export const api = {
  getAuth: () => auth,

  /** Мини-апп открыт кнопкой рассылки: засчитать нажатие (заявка #203). Метку
   *  `bc`/`bb` кладёт в адрес бэк, сервер проверяет её сам. Ошибка не важна. */
  reportBroadcastClick: (broadcastId: string, button: number) =>
    request<{ counted: boolean }>('POST', '/api/client/broadcast-click', {
      broadcast_id: broadcastId,
      button,
    }),

  /**
   * Подтверждать ли покупку биометрией. Пишем и на бэк, и в локальный auth-стейт:
   * иначе экран оплаты, который читает флаг синхронно из `getAuth()`, до следующего
   * входа продолжал бы жить со старым значением.
   */
  setBiometricConfirm: async (enabled: boolean): Promise<void> => {
    await request<{ biometric_confirm_enabled: boolean | null }>(
      'PATCH',
      '/api/client/me/preferences',
      { biometric_confirm_enabled: enabled },
    );
    auth = { ...auth, biometricConfirmEnabled: enabled };
  },

  // GET /api/client/status. Намеренно БЕЗ mock-фолбэка: страница про то, работает ли
  // сервис, и подсунуть здесь «всё зелено» при мёртвом бэке — прямая ложь юзеру.
  networkStatus: () => request<ClientStatusResponse>('GET', '/api/client/status'),

  me: () =>
    tryRequest<ClientProfile>('GET', '/api/client/me', () => {
      throw new ApiError(0, null, 'no fallback');
    }),

  /** Подписки, истёкшие за последние 30 дней: карточка «Продлить» на главной.
   *  Отдельно от `allSubscriptions`, чтобы экраны устройств и профиль не начали
   *  работать с истёкшей. `renewable` — тариф ещё продаётся и это не спецтариф. */
  expiredSubscriptions: async (): Promise<Array<Subscription & { renewable: boolean }>> => {
    const data = await tryRequest<Array<BackendSubscription & { renewable: boolean }>>(
      'GET',
      '/api/client/me/subscriptions/expired',
      () => [],
    );
    return data.map((s) => ({
      ...backendSubToSub(s),
      // Первые минуты после окончания бэк ещё держит `is_active`, а для экрана она
      // уже истекла: статус берём по смыслу ручки.
      status: 'expired' as const,
      renewable: Boolean(s.renewable),
    }));
  },

  /** Все активные подписки клиента в текущем боте (юзер мог купить 2+ разных). */
  allSubscriptions: async (): Promise<Subscription[]> => {
    const data = await tryRequest<BackendSubscription[] | Subscription[]>(
      'GET',
      '/api/client/me/subscriptions',
      () => [] as Subscription[],
    );
    if (data.length === 0) return [];
    if ('planName' in data[0]) return data as Subscription[];
    return (data as BackendSubscription[]).map(backendSubToSub);
  },

  /** Тарифы раздела. `lifetime` — «Навсегда»: один тариф или пусто, если раздел у бота
   * закрыт. Без параметра «На срок», как было. */
  plans: async (
    section: 'term' | 'lifetime' = 'term',
    purpose: 'buy' | 'upgrade' = 'buy',
  ): Promise<Plan[]> => {
    // `purpose=upgrade`: цели апгрейда, где скрытый партнёром срок целиком не прячется
    // (как в боте), а скрытые тарифы и клетки прячутся.
    const path =
      section === 'lifetime'
        ? '/api/client/plans?section=lifetime'
        : purpose === 'upgrade'
          ? '/api/client/plans?purpose=upgrade'
          : '/api/client/plans';
    const data = await tryRequest<BackendPlan[] | Plan[]>(
      'GET',
      path,
      () => [] as Plan[],
    );
    if (data.length === 0) return [];
    if ('priceRub' in data[0]) return data as Plan[];
    return (data as BackendPlan[]).map(backendPlanToPlan);
  },

  devices: async (subscriptionId: string): Promise<Device[]> => {
    const data = await tryRequest<BackendDevice[] | Device[]>(
      'GET',
      `/api/client/subscriptions/${subscriptionId}/devices`,
      () => [] as Device[],
    );
    if (data.length === 0) return [];
    if ('hwid' in data[0]) return (data as BackendDevice[]).map(backendDeviceToDevice);
    return data as Device[];
  },

  deleteDevice: (subscriptionId: string, deviceId: string) =>
    tryRequest<void>(
      'DELETE',
      `/api/client/subscriptions/${subscriptionId}/devices/${deviceId}`,
      () => undefined,
    ),

  // Отключённые устройства: те, кому мы перестали отдавать конфиг после «удалить».
  // Пустой фолбэк — не показывать в dev выдуманные блокировки.
  deviceBlocks: (subscriptionId: string) =>
    tryRequest<BlockedDevice[]>(
      'GET',
      `/api/client/subscriptions/${subscriptionId}/device-blocks`,
      () => [] as BlockedDevice[],
    ),

  // Возврат устройства — обычный `request`: «успех» из мока означал бы, что человек
  // считает телефон разблокированным, а тот продолжает получать заглушку.
  unblockDevice: (subscriptionId: string, hwid: string) =>
    request<void>('POST', `/api/client/subscriptions/${subscriptionId}/device-blocks/unblock`, {
      hwid,
    }),

  freezeSubscription: (subscriptionId: string) =>
    tryRequest<{ id: string; is_frozen: boolean }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/freeze`,
      () => ({ id: subscriptionId, is_frozen: true }),
    ),

  unfreezeSubscription: (subscriptionId: string) =>
    tryRequest<{ id: string; is_frozen: boolean }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/unfreeze`,
      () => ({ id: subscriptionId, is_frozen: false }),
    ),

  // Отзыв публичной ссылки. Мутация, поэтому mock-фолбэка не будет даже в dev:
  // «успех» при упавшем бэке здесь означал бы, что человек считает ссылку
  // отозванной, а она продолжает работать.
  rotateSubscriptionLink: (subscriptionId: string) =>
    tryRequest<{ id: string; subscription_url: string; rotated_at: string }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/rotate-link`,
      () => ({ id: subscriptionId, subscription_url: '', rotated_at: '' }),
    ),

  toggleAutoRenew: (subscriptionId: string, enabled: boolean, days?: number | null) =>
    tryRequest<{ id: string; auto_renew_enabled: boolean; auto_renew_days: number | null }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/auto-renew`,
      () => ({ id: subscriptionId, auto_renew_enabled: enabled, auto_renew_days: days ?? null }),
      { enabled, days: days ?? null },
    ),

  // ===== Payments =====

  paymentProviders: () =>
    tryRequest<{
      providers: string[];
      fee_percent?: number;
      /** Ставка каждого способа, когда сбор не в цене: для пометки у способа. */
      provider_fee_percent?: Record<string, number>;
    }>(
      'GET',
      '/api/client/payments/providers',
      () => ({
        providers: ['antilopay', 'aurapay', 'stars', 'cryptobot'],
        provider_fee_percent: { antilopay: 5, aurapay: 5.7, cryptobot: 3 },
      }),
    ),

  createPaymentIntent: (body: { plan_uuid: string; provider: string; idempotency_key?: string }) =>
    tryRequest<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      base_amount_minor: number;
      fee_minor: number;
      gross_amount_minor: number;
      currency: string;
      promo_exhausted?: boolean;
    }>(
      'POST',
      '/api/client/payments/intent',
      () => ({
        payment_id: `mock_${Date.now()}`,
        provider: body.provider,
        invoice_url: 'https://example.com/mock-invoice',
        expires_at: new Date(Date.now() + 3600_000).toISOString(),
        base_amount_minor: 15000,
        fee_minor: 0,
        gross_amount_minor: 15000,
        currency: 'RUB',
      }),
      body,
    ),

  paymentStatus: (paymentId: string) =>
    tryRequest<{
      payment_id: string;
      status: string;
      subscription_id: string | null;
      intent_type: string | null;
      /** done | pending | deferred — см. PaymentStatusResponse на бэке. */
      fulfillment?: string | null;
      /** Звёзд в счёте, у оплаты звёздами. */
      stars?: number | null;
      /** Бот партнёра на Хексе, где купить ровно столько звёзд. Только у неоплаченного. */
      stars_shop_url?: string | null;
    }>(
      'GET',
      `/api/client/payments/${paymentId}/status`,
      // Mock NIKOGDA не возвращает 'paid' — иначе фронт думает что юзер купил пока бэк лежит
      // (false-positive success). При недоступном API processing-экран покажет error state.
      () => ({ payment_id: paymentId, status: 'pending', subscription_id: null, intent_type: null }),
    ),

  // Activity proxy /subs/requests
  activity: async (subscriptionId: string): Promise<ActivityLog[]> => {
    const data = await tryRequest<
      | Array<{
          id: number;
          device_id: number | null;
          hwid: string | null;
          status: string;
          ip: string | null;
          created_at: string;
        }>
      | ActivityLog[]
    >(
      'GET',
      `/api/client/subscriptions/${subscriptionId}/activity`,
      () => [] as ActivityLog[],
    );
    if (data.length === 0) return [];
    if ('deviceName' in data[0]) return data as ActivityLog[];
    return (
      data as Array<{
        id: number;
        device_id: number | null;
        hwid: string | null;
        status: string;
        ip: string | null;
        created_at: string;
      }>
    ).map((r) => ({
      id: String(r.id),
      status: r.status === 'success' ? 'success' : 'failed',
      deviceId: r.device_id ? String(r.device_id) : '',
      // Имя устройства подставляет экран по `deviceId`; здесь только HWID на крайний
      // случай. Английское «Device» отсюда уходило человеку как есть.
      deviceName: r.hwid || '',
      ip: r.ip || '',
      timestamp: r.created_at,
    }));
  },

  // Расширенный профиль клиента (баланс + рефералы)
  profile: () =>
    tryRequest<{
      id: string;
      tg_user_id: number;
      tg_username: string | null;
      email: string | null;
      /** Главная витрины бота; null — витрины нет, «Доступ с сайта» не показываем. */
      site_url?: string | null;
      /** Можно открыть кабинет на сайте без пароля (`siteLink`, заявка #142). */
      site_login_available?: boolean;
      locale: string;
      balance_usd_minor: number;
      balance_display_minor: number;
      display_currency: string;
      referrals_invited: number;
      referral_link: string | null;
      referral_l1_percent: number;
      referral_l2_percent: number;
      referral_enabled: boolean;
      active_promo_code_id: string | null;
    }>(
      'GET',
      '/api/client/me/profile',
      () => ({
        id: '',
        tg_user_id: 0,
        tg_username: null,
        email: null,
        site_url: null,
        site_login_available: false,
        locale: 'ru',
        balance_usd_minor: 0,
        balance_display_minor: 0,
        display_currency: 'RUB',
        referrals_invited: 0,
        referral_link: null,
        referral_l1_percent: 15,
        referral_l2_percent: 5,
        referral_enabled: true,
        active_promo_code_id: null,
      }),
    ),

  /**
   * Одноразовая ссылка в кабинет на сайте-витрине без пароля (заявка #142). Голый
   * `request`, а не `tryRequest`: тот превращает 403 в «бот недоступен» на весь мини-апп,
   * а у этой ручки отказы свои (404 витрины нет, 429 слишком часто).
   */
  siteLink: () => request<{ url: string }>('POST', '/api/client/me/site-link'),

  /**
   * Привязка почты, чтобы заходить в кабинет на сайте без Telegram.
   *
   * Два шага: код уходит на указанный адрес, подтверждение записывает почту и
   * пароль. Пока не подтверждено, у клиента ничего не меняется.
   *
   * Мока здесь нет намеренно (`request`, а не `tryRequest`): молчаливый успех на
   * фейковых данных человек прочитает как «почта привязана», а войти на сайте
   * потом не сможет.
   */
  linkEmailRequest: (email: string, password: string) =>
    request<{ ok: boolean; merge?: boolean }>('POST', '/api/client/web/auth/link/request', {
      email,
      password,
    }),

  linkEmailConfirm: (code: string) =>
    request<{ ok: boolean; merged?: boolean }>('POST', '/api/client/web/auth/link/confirm', {
      code,
    }),

  // Промокоды. Одно поле на два вида кодов: `kind: 'voucher'` — это подарочный код
  // партнёра (бесплатная подписка), он не даёт скидку, а активируется отдельно.
  applyPromo: (code: string) =>
    tryRequest<{
      kind?: 'promo' | 'voucher';
      promo_code_id: string;
      code: string;
      discount_type: string;
      discount_value: number;
      display_currency: string;
      usd_to_display_rate: number;
      plan_name?: string | null;
    }>(
      'POST',
      '/api/client/promo/apply',
      () => ({
        kind: 'promo' as const,
        promo_code_id: 'mock_promo',
        code: code.toUpperCase(),
        discount_type: 'percent',
        discount_value: 10,
        display_currency: 'RUB',
        usd_to_display_rate: 100,
      }),
      { code },
    ),

  redeemVoucher: (code: string) =>
    tryRequest<{ subscription_id: string; plan_name: string }>(
      'POST',
      '/api/client/promo/voucher/redeem',
      () => ({ subscription_id: 'mock_sub', plan_name: 'Стандарт' }),
      { code },
    ),

  clearPromo: () =>
    tryRequest<{ ok: boolean }>(
      'DELETE',
      '/api/client/promo/active',
      () => ({ ok: true }),
    ),

  // Gift flow
  createGift: (body: { plan_uuid: string; provider: string }) =>
    tryRequest<{
      gift_id: string;
      token: string;
      share_url: string;
      payment_id: string;
      invoice_url: string;
      expires_at: string | null;
      gross_amount_minor: number;
      currency: string;
    }>(
      'POST',
      '/api/client/gifts/create',
      () => ({
        gift_id: `mock_${Date.now()}`,
        token: Math.random().toString(36).slice(2, 14),
        share_url: `https://t.me/?start=gift_mock_${Date.now()}`,
        payment_id: `mock_pay_${Date.now()}`,
        invoice_url: 'https://example.com/mock-gift-invoice',
        expires_at: new Date(Date.now() + 3600_000).toISOString(),
        gross_amount_minor: 15000,
        currency: 'RUB',
      }),
      body,
    ),

  // Подарок с баланса профиля — синхронно, без счёта. request (не tryRequest):
  // реальное списание нельзя маскировать mock'ом; 402 = не хватает — пробрасываем.
  createGiftFromBalance: (planUuid: string) =>
    request<{
      gift_id: string;
      token: string;
      share_url: string;
      subscription_id: string;
      balance_after_usd_minor: number;
      charged_display_minor: number;
      currency: string;
    }>('POST', '/api/client/gifts/create-from-balance', { plan_uuid: planUuid }),

  myGifts: () =>
    tryRequest<
      Array<{
        id: string;
        token: string;
        status: string;
        plan_uuid: string;
        plan_name: string;
        share_url: string | null;
        created_at: string;
        paid_at: string | null;
        claimed_at: string | null;
        /** «@nick» или «ID 123456789». null, пока подарок не забрали. */
        claimed_by_label: string | null;
      }>
    >('GET', '/api/client/gifts', () => []),

  // Traffic top-up
  trafficPacks: (subscriptionId: string) =>
    tryRequest<{
      packs: Array<{
        gb: number;
        cost_usd_minor: number;
        cost_display: number;
        /** К оплате счётом: со сбором, если партнёр встроил его в цену. */
        total_display?: number;
        fee_display_minor?: number;
        currency: string;
      }>;
    }>(
      'GET',
      `/api/client/subscriptions/${subscriptionId}/traffic/packs`,
      () => ({ packs: [] as Array<{ gb: number; cost_usd_minor: number; cost_display: number; currency: string }> }),
    ),

  createTrafficIntent: (
    subscriptionId: string,
    body: { gb_amount: number; provider: string },
  ) =>
    tryRequest<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      gross_amount_minor: number;
      currency: string;
    }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/traffic-intent`,
      () => ({
        payment_id: `mock_traffic_${Date.now()}`,
        provider: body.provider,
        invoice_url: 'https://example.com/mock-traffic-invoice',
        expires_at: new Date(Date.now() + 3600_000).toISOString(),
        gross_amount_minor: 5000,
        currency: 'RUB',
      }),
      body,
    ),

  // ---------- Докупки к «Навсегда»: трафик обхода и +15 устройств ----------
  lifetimeAddons: (subscriptionId: string) =>
    request<{
      offers: LifetimeAddonOffer[];
      balance_usd_minor: number;
    }>('GET', `/api/client/subscriptions/${subscriptionId}/addons`),

  createAddonIntent: (
    subscriptionId: string,
    body: { kind: 'traffic' | 'devices'; key: string; provider: string; nonce: string },
  ) =>
    request<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      gross_amount_minor: number;
      currency: string;
    }>('POST', `/api/client/subscriptions/${subscriptionId}/addon-intent`, body),

  buyAddonFromBalance: (
    subscriptionId: string,
    body: { kind: 'traffic' | 'devices'; key: string; nonce: string },
  ) =>
    request<{ subscription_id: string; devices: number; traffic_limit_bytes: number | null }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/addon-from-balance`,
      body,
    ),

  // ---------- Renew (продление за деньги) ----------
  renewSubscription: (subscriptionId: string, provider: string, days?: number | null) =>
    tryRequest<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      gross_amount_minor: number;
      currency: string;
    }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/renew`,
      () => ({
        payment_id: `mock_renew_${Date.now()}`,
        provider,
        invoice_url: 'https://example.com/mock-renew-invoice',
        expires_at: new Date(Date.now() + 3600_000).toISOString(),
        gross_amount_minor: 30000,
        currency: 'RUB',
      }),
      { provider, days: days ?? null },
    ),

  /** Цена продления до оплаты: на период тарифа (`days` не задан) или на N дней. */
  renewQuote: (subscriptionId: string, days?: number | null) =>
    request<RenewQuote>(
      'GET',
      `/api/client/subscriptions/${subscriptionId}/renew-quote${
        days != null ? `?days=${days}` : ''
      }`,
    ),

  // ---------- Upgrade (balance-paid) ----------
  // Сколько будет стоить переход. Считает бэк тем же кодом, что потом спишет деньги,
  // поэтому сумма на кнопке и сумма в чеке всегда совпадают.
  upgradeQuote: (subscriptionId: string, newPlanUuid: string) =>
    request<{
      new_plan_uuid: string;
      plan_name: string;
      devices: number;
      remaining_days: number;
      charged_usd_minor: number;
      charged_display_minor: number;
      display_currency: string;
      balance_usd_minor: number;
      enough_balance: boolean;
    }>('POST', `/api/client/subscriptions/${subscriptionId}/upgrade/quote`, {
      new_plan_uuid: newPlanUuid,
    }),

  // request (не tryRequest): апгрейд списывает деньги, и подменять его ошибку
  // mock-успехом нельзя — человек решит, что тариф сменился.
  upgradeSubscription: (subscriptionId: string, newPlanUuid: string) =>
    request<{
      id: string;
      plan_uuid: string;
      devices: number;
      end_date: string;
      balance_after_usd_minor: number;
      charged_usd_minor: number;
      charged_display_minor: number;
      display_currency: string;
    }>('POST', `/api/client/subscriptions/${subscriptionId}/upgrade`, {
      new_plan_uuid: newPlanUuid,
    }),

  // #4 — купить новый план напрямую с баланса (без провайдера). request (не tryRequest):
  // реальную покупку нельзя маскировать mock'ом, ошибки пробрасываем (402 = не хватает).
  buyFromBalance: (planUuid: string) =>
    request<{ subscription_id: string; balance_after_usd_minor: number }>(
      'POST',
      '/api/client/subscriptions/buy-from-balance',
      { plan_uuid: planUuid },
    ),

  // Продлить подписку с баланса (без провайдера, синхронно). request (не tryRequest):
  // реальное списание нельзя маскировать mock'ом; 402 = не хватает — пробрасываем.
  renewFromBalance: (subscriptionId: string, days?: number | null) =>
    request<{
      subscription_id: string;
      end_date: string;
      balance_after_usd_minor: number;
      days: number | null;
      charged_display_minor: number;
      promo_code: string | null;
    }>(
      'POST',
      `/api/client/subscriptions/${subscriptionId}/renew-from-balance`,
      { days: days ?? null },
    ),

  // ---------- Пробный период (триал) ----------
  // status: GET с tryRequest (фейл бэка → CTA не показываем). claim: POST с request
  // (ошибки наверх — обрабатываем в компоненте).
  trialStatus: () =>
    tryRequest<{
      available: boolean;
      reason: string;
      days: number;
      devices: number;
      /** Можно взять пробник «Навсегда» вместо обычного: пробник один на человека. */
      lifetime_available?: boolean;
      lifetime_days?: number;
      lifetime_devices?: number;
      /** Пробник только подписчикам этих каналов (#227). Пусто — без условия. */
      channels?: { url: string; name: string }[];
    }>(
      'GET',
      '/api/client/trial/status',
      () => ({ available: false, reason: 'disabled', days: 0, devices: 0 }),
    ),

  claimTrial: (section: 'term' | 'lifetime' = 'term') =>
    request<{ subscription_id: string; subscription_url: string | null; plan_name: string }>(
      'POST',
      section === 'lifetime' ? '/api/client/trial/claim?section=lifetime' : '/api/client/trial/claim',
    ),

  // ---------- Top-up баланса ----------
  topupBalance: (amountDisplayMinor: number, provider: string) =>
    tryRequest<{
      payment_id: string;
      provider: string;
      invoice_url: string;
      expires_at: string | null;
      gross_amount_minor: number;
      currency: string;
    }>(
      'POST',
      '/api/client/payments/topup',
      () => ({
        payment_id: `mock_topup_${Date.now()}`,
        provider,
        invoice_url: 'https://example.com/mock-topup-invoice',
        expires_at: new Date(Date.now() + 3600_000).toISOString(),
        gross_amount_minor: amountDisplayMinor,
        currency: 'RUB',
      }),
      { amount_display_minor: amountDisplayMinor, provider },
    ),

  // ---------- История платежей ----------
  //
  // Лента объединённая: счета провайдеров (`source: 'payment'`) и движения внутреннего
  // баланса (`source: 'wallet'`). Раньше показывались только счета, и всё купленное с
  // баланса не попадало в историю вообще — человек видел «Пока нет платежей», имея
  // покупки. Склейку (чтобы провайдерская покупка не показалась дважды) делает бэк.
  paymentsHistory: () =>
    tryRequest<{
      items: Array<{
        id: string;
        source: 'payment' | 'wallet';
        provider: string;
        status: string;
        intent_type: string | null;
        amount_display_minor: number;
        amount_usd_minor: number;
        currency: string;
        /** +1 — деньги пришли, −1 — ушли. */
        signed: number;
        created_at: string;
        paid_at: string | null;
      }>;
    }>(
      'GET',
      '/api/client/payments/history',
      () => ({ items: [] }),
    ),

  // ---------- Цена тарифа со скидкой (считает сервер) ----------
  //
  // Фронт больше НЕ считает скидку сам. Раньше считал (`цена × (1 − процент)`), а бэк
  // применял свои правила, и экран показывал «0 ₽» там, где счёт выставлялся на другую
  // сумму. Источник правды один, и это сервер.
  priceQuote: (planUuid: string) =>
    request<{
      plan_uuid: string;
      base_display_minor: number;
      discount_display_minor: number;
      final_display_minor: number;
      display_currency: string;
      /** К оплате счётом: со сбором, если партнёр встроил его в цену. */
      total_display_minor?: number;
      /** Сбор внутри total_display_minor. Ноль — надбавки в цене нет. */
      fee_display_minor?: number;
      promo_code: string | null;
      promo_blocked_reason: string | null;
      is_free: boolean;
      /** Хватает ли баланса на списание: сравнение на сервере в USD-центах. */
      balance_enough: boolean;
      /** Сумма счёта по каждому способу оплаты, со сбором способа (minor). Без звёзд. */
      provider_totals?: Record<string, number>;
    }>('GET', `/api/client/payments/quote?plan_uuid=${encodeURIComponent(planUuid)}`),

  // ---------- Loyalty / милстоуны ----------
  loyalty: () =>
    tryRequest<{
      total_renewals: number;
      achieved: Array<{
        renewal_count: number;
        bonus_basis_points: number;
        title: string;
        emoji: string;
      }>;
      next: {
        renewal_count: number;
        bonus_basis_points: number;
        title: string;
        emoji: string;
      } | null;
      renewals_to_next: number | null;
      /** Вся лестница по порядку, чтобы показать, куда идёт клиент. */
      levels: Array<{
        renewal_count: number;
        bonus_basis_points: number;
        title: string;
        emoji: string;
      }>;
    }>(
      'GET',
      '/api/client/me/loyalty',
      () => ({ total_renewals: 0, achieved: [], next: null, renewals_to_next: null, levels: [] }),
    ),

  // ---------- Отзывы ----------
  // Фолбэк — выключенная витрина, а не выдуманные отзывы: показать в dev пять
  // фейковых мнений о чужом сервисе хуже, чем не показать ничего.
  reviews: (params?: { filter?: ReviewFilter; cursor?: string }) => {
    const qs = new URLSearchParams();
    if (params?.filter && params.filter !== 'all') qs.set('filter', params.filter);
    if (params?.cursor) qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs}` : '';
    return tryRequest<ReviewsPage>('GET', `/api/client/reviews${suffix}`, () => ({
      enabled: false,
      config: { glyph: 'shield-check', verified_badge: true, promo: { enabled: false, title: '', subtitle: '' } },
      summary: { average: 0, total: 0, positive: 0, negative: 0, distribution: {}, positive_share: 0 },
      items: [],
      next_cursor: null,
      has_more: false,
      my_review: null,
      review_target: null,
      review_targets: [],
    }));
  },

  // Отправка отзыва — обычный `request`, без mock-фолбэка: мутация, которая
  // «прошла» в моке, врала бы клиенту, что отзыв опубликован.
  createReview: (body: {
    subscription_id: string;
    rating: number;
    text: string | null;
    /** Указать на витрине, что подписка досталась бесплатно или по коду. */
    disclose_acquisition?: boolean;
  }) =>
    request<Review>('POST', '/api/client/reviews', body),

  // ---------- Info (about / faq / support_url) ----------
  info: () =>
    tryRequest<{
      about_text: string;
      faq_text: string;
      features: { icon: string; text: string }[];
      support_url: string | null;
      bot_username: string | null;
      /** Кнопки партнёра по разделам menu / profile / subs. Ровно одно из url/path. */
      custom_buttons: Record<string, { text: string; url: string | null; path: string | null }[]>;
      /** Пользовательское соглашение и политика (#208): ссылки партнёра или шаблоны. */
      terms_url?: string | null;
      privacy_url?: string | null;
    }>(
      'GET',
      '/api/client/info',
      () => ({
        about_text: 'VPN сервис для безопасного доступа в интернет.',
        faq_text: 'Часто задаваемые вопросы появятся скоро.',
        features: [
          { icon: 'infinity', text: 'Безлимитный трафик на всех локациях' },
          { icon: 'devices', text: 'Несколько устройств одновременно' },
          { icon: 'instant', text: 'Мгновенная активация после оплаты' },
          { icon: 'servers', text: 'Серверы в разных странах' },
          { icon: 'bypass', text: 'Работает там, где блокируют' },
          { icon: 'no_logs', text: 'Без логов трафика' },
        ],
        support_url: null,
        bot_username: null,
        custom_buttons: {},
      }),
    ),

  // ---------- Язык ----------
  // PATCH /api/client/me/locale — выбор клиента важнее language_code из Telegram:
  // человек мог поставить русский интерфейс телефона, но хотеть английские сообщения.
  setLocale: (locale: string) =>
    request<{ locale: string }>('PATCH', '/api/client/me/locale', { locale }),

  // ---------- Розыгрыши ----------
  giveaways: () =>
    tryRequest<Giveaway[]>('GET', '/api/client/giveaways', () => []),

  joinGiveaway: (giveawayId: string) =>
    request<Giveaway>('POST', `/api/client/giveaways/${giveawayId}/join`),

  // ---------- Locations (доступные страны VPN) ----------
  locations: (section: 'term' | 'lifetime' = 'term') =>
    tryRequest<{
      regions: Record<string, { flag: string; country_ru: string; country_code?: string | null }[]>;
      total: number;
      synced_at: string | null;
    }>(
      'GET',
      section === 'lifetime' ? '/api/client/locations?section=lifetime' : '/api/client/locations',
      () => ({ regions: {}, total: 0, synced_at: null }),
    ),

  // ---------- Страны в подписке (заявка #169) ----------
  // Без мок-фолбэка: экран выбора не должен показывать выдуманный список стран.
  subscriptionCountries: (subscriptionId: string) =>
    request<SubscriptionCountries>('GET', `/api/client/subscriptions/${subscriptionId}/countries`),
  saveSubscriptionCountries: (subscriptionId: string, hidden: string[]) =>
    request<SubscriptionCountries>('PUT', `/api/client/subscriptions/${subscriptionId}/countries`, {
      hidden,
    }),

  // ---------- Вывод реферального заработка ----------
  //
  // Фолбэк — выключенный вывод, а не выдуманные суммы: мок «можно вывести 1 000 ₽»
  // человек принял бы за свои деньги.
  referralWithdrawState: () =>
    tryRequest<WithdrawState>('GET', '/api/client/referral/withdrawals', () => ({
      enabled: false,
      methods: [],
      currency: 'RUB',
      min_display_minor: 0,
      earned_display_minor: 0,
      available_display_minor: 0,
      open: null,
      history: [],
    })),

  // Мутации — голый `request`, не `tryRequest`: тот превращает любой 403 в
  // BotDisabledError, и отказ `app.referral_withdraw.disabled` закрыл бы весь мини-апп
  // экраном «бот недоступен». Здесь 403 значит только «вывод выключен».
  referralWithdrawQuote: (method: WithdrawMethod, amountDisplayMinor: number) =>
    request<WithdrawQuote>(
      'GET',
      `/api/client/referral/withdrawals/quote?method=${encodeURIComponent(method)}&amount_display_minor=${amountDisplayMinor}`,
    ),

  createReferralWithdrawal: (body: CreateWithdrawalBody) =>
    request<WithdrawState>('POST', '/api/client/referral/withdrawals', body),

  cancelReferralWithdrawal: (withdrawalId: string) =>
    request<WithdrawState>(
      'POST',
      `/api/client/referral/withdrawals/${encodeURIComponent(withdrawalId)}/cancel`,
    ),
};

/** Докупка к «Навсегда». `cost_display` — с баланса, `total_display` — счётом (со сбором,
 * если партнёр встроил его в цену). */
export interface LifetimeAddonOffer {
  kind: 'traffic' | 'devices';
  key: string;
  gb: number | null;
  devices: number | null;
  cost_usd_minor: number;
  cost_display: number;
  total_display: number;
  fee_display_minor: number;
  currency: string;
}
